import { Add, Delete, Download } from '@mui/icons-material';
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  deleteQRCode,
  getEntityQRCodes,
  updateQRCode,
  upsertEntityQRCodes,
} from 'api/qrCodes';
import MobilePanelList from 'components/lists/MobilePanelList';
import { TAB_LAYOUT_HEADER_HEIGHT } from 'components/layout/FormTabNavigation';
import { AppContext } from 'context/appContext';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import {
  useDefaultTableOptions,
  TableType,
} from 'hooks/useDefaultTableOptions';
import { useTranslation } from 'react-i18next';
import {
  MaterialReactTable,
  MRT_SortingState,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ActionFunction,
  Outlet,
  useFetcher,
  useLoaderData,
  useLocation,
  useNavigate,
  useOutletContext,
} from 'react-router';
import { ROUTES, routeWithParams } from 'router/routes';
import {
  DisplayMode,
  EntityPanelActionHandler,
  EntityTypes,
  FileMetadata,
  FormMode,
  PanelRootContext,
  Property,
  PropertyGroup,
  TraxeroActionResponse,
} from 'types';
import { getBlankQRCode } from 'utils';
import MRTableToolbar from 'components/tables/PanelTableToolbar';

import { useFetcherKey } from 'hooks/useFetcherKey';
import FormFrame from 'components/layout/FormFrame';
export interface QRCodeLoaderData {
  _qrCodes: Promise<FileMetadata[]>;
}

export interface QRCodeRouteParams {
  params: {
    qrCodeID?: string | undefined;
    propertyID?: string | undefined;
    groupID?: string | undefined;
    tenantID?: string | undefined;
  };
}

export const qrCodesLoader = ({
  params: { tenantID, groupID, propertyID },
}: QRCodeRouteParams) => {
  if (propertyID && tenantID) {
    return {
      _qrCodes: getEntityQRCodes({
        tenantID,
        entityType: EntityTypes.Property,
        entityID: propertyID,
        body: '',
        qrCodeID: '',
      }),
    };
  } else if (groupID && tenantID) {
    return {
      _qrCodes: getEntityQRCodes({
        tenantID,
        entityType: EntityTypes.PropertyGroup,
        entityID: groupID,
        body: '',
        qrCodeID: '',
      }),
    };
  } else if (tenantID) {
    return {
      _qrCodes: getEntityQRCodes({
        tenantID,
        entityType: EntityTypes.Tenant,
        entityID: tenantID,
        body: '',
        qrCodeID: '',
      }),
    };
  } else {
    return {
      _qrCodes: Promise.resolve([]),
    };
  }
};

export interface QRCodeContext {
  entity: Property | PropertyGroup;
  qrCode: FileMetadata;
  setQRCode: (qr: FileMetadata) => void;
  qrCodes: FileMetadata[];
  onBack: () => void;
  displayMode: DisplayMode;
  formMode: FormMode;
  fetcherKey: string;
  handleDownloadQRCode: (f: FileMetadata) => void;
}

export const QRCodesPanel = (): ReactNode => {
  const { t } = useTranslation();
  const { breakpoints } = useTheme();
  const mobile = useMediaQuery(breakpoints.down('lg'));
  const { state } = useContext(AppContext);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { fetcherKey } = useFetcherKey();
  const fetcher = useFetcher({ key: fetcherKey });
  const displayToast = useContext(ToastContext);
  const { formMode, setFormMode, entity, displayMode } =
    useOutletContext<PanelRootContext<Property | PropertyGroup>>();
  const { _qrCodes } = useLoaderData() as QRCodeLoaderData;

  const [qrCodes, setQRCodes] = useState<FileMetadata[]>(entity?.qrCodes || []);
  const [qrCode, setQRCode] = useState<FileMetadata>(
    getBlankQRCode(state.tenant.id, entity.id),
  );
  const [isLoading, setIsLoading] = useState(true);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);

  const canAddRemove = useMemo<boolean>(() => {
    if (displayMode === 'property') {
      return state.entityPermissions.property.update;
    } else if (displayMode === 'group') {
      return state.entityPermissions.property_group.update;
    } else if (displayMode === 'tenant') {
      return state.entityPermissions.tenant.update;
    }
    return false;
  }, [state.entityPermissions, displayMode]);

  useEffect(() => {
    const awaitLoaderData = async () => {
      const qrCodes = await _qrCodes;
      setQRCodes(qrCodes);
      setIsLoading(false);
    };
    awaitLoaderData();
  }, [_qrCodes]);

  const getSuccessMessage = useCallback(
    (res: TraxeroActionResponse<EntityPanelActionHandler>): string => {
      switch (res.handler) {
        case EntityPanelActionHandler.UPDATE:
          return t('update_success', {
            ns: 'toasts',
            name: `${t('qrCodes.label', { ns: 'entities' })}`,
          });
        case EntityPanelActionHandler.DELETE:
          return t('delete_success', {
            ns: 'toasts',
            name: `${t('qrCodes.label', { ns: 'entities' })}`,
          });
        case EntityPanelActionHandler.PROPERTY_CREATE:
        case EntityPanelActionHandler.GROUP_CREATE:
          return t('create_success', {
            ns: 'toasts',
            name: `${t('qrCodes.label', { ns: 'entities' })}`,
          });
        case EntityPanelActionHandler.PROPERTY_UPSERT:
          return t('update_success', {
            ns: 'toasts',
            name: `${t('property.label', { ns: 'entities' })}`,
          });
        case EntityPanelActionHandler.GROUP_UPSERT:
          return t('update_success', {
            ns: 'toasts',
            name: `${t('property_group.label', { ns: 'entities' })}`,
          });
        default:
          return '';
      }
    },
    [],
  );
  const getErrorMessage = useCallback(
    (res: TraxeroActionResponse<EntityPanelActionHandler>): string => {
      const { handler, status, statusText } = res;
      switch (handler) {
        case EntityPanelActionHandler.UPDATE:
          return t('update_error', {
            ns: 'toasts',
            name: `${t('qrCodes.label', { ns: 'entities' })}`,
            error: `${status} - ${statusText}`,
          });
        case EntityPanelActionHandler.DELETE:
          return t('delete_error', {
            ns: 'toasts',
            name: `${t('qrCodes.label', { ns: 'entities' })}`,
            error: `${status} - ${statusText}`,
          });
        case EntityPanelActionHandler.PROPERTY_CREATE:
        case EntityPanelActionHandler.GROUP_CREATE:
          return t('create_error', {
            name: `${t('qrCodes.label', { ns: 'entities' })}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.PROPERTY_UPSERT:
          return t('update_error', {
            name: `${t('property.label', { ns: 'entities' })}${t(
              'qrCodes.label_plural',
              { ns: 'entities' },
            )}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.GROUP_UPSERT:
          return t('update_error', {
            name: `${t('property_group.label', { ns: 'entities' })}${t(
              'qrCodes.label_plural',
              { ns: 'entities' },
            )}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        default:
          return t('unknown_error', { ns: 'toasts' });
      }
    },
    [],
  );
  const response =
    fetcher.data as TraxeroActionResponse<EntityPanelActionHandler> | null;
  useEffect(() => {
    if (response) {
      if (response.status < 400) {
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: getSuccessMessage(response),
        });
        handleCancelAction();
      } else {
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: getErrorMessage(response),
        });
      }
    }
  }, [fetcher]);

  const handleCancelAction = useCallback(() => {
    setFormMode('none');
    setQRCode(getBlankQRCode(state.tenant.id, entity.id));
    // resolve the list route explicitly -- reading the current pathname here
    // would point at the form route the user is trying to leave
    const listRoute = getActionRouteForDisplayMode({
      displayMode,
      tenantID: state.tenant.id,
      entityID: entity.id,
    });
    if (listRoute) {
      navigate(listRoute, { replace: true });
    }
  }, [displayMode, state.tenant.id, entity.id, navigate]);
  const handleCreateClick = () => {
    setFormMode('create');
    setQRCode(getBlankQRCode(state.tenant.id, entity.id));
    if (displayMode === 'property') {
      navigate(
        routeWithParams(ROUTES.PROPERTY_QR_CODES_FORM, [
          state.tenant.id,
          entity.id,
          0,
        ]),
      );
    } else if (displayMode === 'group') {
      navigate(
        routeWithParams(ROUTES.PROPERTY_GROUP_QR_CODES_FORM, [
          state.tenant.id,
          entity.id,
          0,
        ]),
      );
    }
  };
  const handleDownloadQRCode = (file: FileMetadata | undefined) => {
    if (!file) return;
    const downloadLink = document.createElement('a');
    downloadLink.href = file.downloadFileURL || '';
    downloadLink.download = file.fileKey || '';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };
  const handleDeleteQRCode = (file: FileMetadata | undefined) => {
    if (!file) return;
    fetcher.submit(
      {
        body: JSON.stringify({
          ...file,
        }),
        handler: EntityPanelActionHandler.DELETE,
      },
      {
        method: 'POST',
        action: pathname,
        encType: 'application/json',
      },
    );
  };
  const handleQRCodeClick = (id: string | undefined) => {
    if (!id) return;
    const selected: FileMetadata | undefined = qrCodes.find(
      (p) => p.fileKey === id,
    );
    if (selected) {
      setQRCode({ ...selected, entityID: entity.id });
      setFormMode('edit');
      navigate(
        routeWithParams(
          displayMode === 'property'
            ? ROUTES.PROPERTY_QR_CODES_FORM
            : ROUTES.PROPERTY_GROUP_QR_CODES_FORM,
          [state.tenant.id, entity?.id, selected?.fileKey || 0],
        ),
      );
    }
  };

  const columns = useMemo<MRT_ColumnDef<FileMetadata>[]>(() => {
    const _columns: MRT_ColumnDef<FileMetadata>[] = [
      {
        header: t('qrCodes.name', { ns: 'entities' }),
        accessorKey: 'data.title',
      },
      {
        header: t('qrCodes.description', { ns: 'entities' }),
        accessorKey: 'data.description',
      },
      {
        header: t('qrCodes.created', { ns: 'entities' }),
        accessorKey: 'created',
      },
    ];
    return _columns;
  }, [displayMode]);

  const tableDefaults = useDefaultTableOptions<FileMetadata>(TableType.NESTED);
  const table = useMaterialReactTable({
    ...tableDefaults,
    data: qrCodes,
    columns,
    enableRowSelection: false,
    initialState: {
      density: 'compact',
      showColumnFilters: false,
      showGlobalFilter: true,
      columnVisibility: {
        id: false,
        name: true,
        description: true,
        created: true,
      },
      columnPinning: {
        right: ['mrt-row-actions'],
      },
    },
    state: {
      isLoading,
      sorting,
      showLoadingOverlay: false,
      showSkeletons: false,
    },
    onSortingChange: setSorting,
    getRowId: (row) => String(row.fileKey),
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: 'white',
        cursor: 'pointer',
      },
      onClick: () => handleQRCodeClick(row.original.fileKey),
    }),
    renderTopToolbar: ({ table }) => (
      <MRTableToolbar
        table={table}
        optionsDisabled={!canAddRemove}
        tableType={TableType.NESTED}
        isSubmitting={fetcher.state === 'submitting'}
        menuOptions={[
          {
            onClick: () => handleCreateClick(),
            content: t('buttons.create', {
              name: t('qrCodes.label', { ns: 'entities' }),
            }),
            disabled: !canAddRemove,
            icon: <Add />,
          },
        ]}
      >
        <Stack
          direction={'row'}
          flexGrow={1}
          justifyContent={'flex-start'}
          alignItems={'center'}
        >
          <Typography variant='h3' textTransform={'capitalize'}>
            {t('qrCodes.label_plural', { ns: 'entities' })}
          </Typography>
        </Stack>
      </MRTableToolbar>
    ),
    renderRowActionMenuItems: ({ closeMenu, row }) => {
      const options = [
        <MenuItem
          key={1}
          divider
          onClick={() => {
            handleDownloadQRCode(row.original);
            closeMenu();
          }}
          sx={{ m: 0, backgroundColor: 'white' }}
        >
          <ListItemIcon>
            <Download />
          </ListItemIcon>
          {t('buttons.download', {
            name: `${t('qrCodes.label', { ns: 'entities' })}`,
          })}
        </MenuItem>,
        <MenuItem
          key={2}
          divider
          onClick={() => {
            handleDeleteQRCode(row.original);
            closeMenu();
          }}
          sx={{ m: 0, backgroundColor: 'white' }}
        >
          <ListItemIcon>
            <Delete />
          </ListItemIcon>
          {t('buttons.delete', {
            name: `${t('qrCodes.label', { ns: 'entities' })}`,
          })}
        </MenuItem>,
      ];
      return options;
    },
  });

  if (formMode !== 'none') {
    return (
      <Outlet
        context={
          {
            qrCode,
            setQRCode,
            qrCodes,
            onBack: handleCancelAction,
            formMode,
            displayMode,
            handleDownloadQRCode,
            fetcherKey,
            entity,
          } satisfies QRCodeContext
        }
      />
    );
  }

  if (mobile) {
    const itemHeight = 70;
    const renderItem = (item: FileMetadata, index: number) => {
      return (
        <ListItem
          key={index}
          onClick={() => handleQRCodeClick(item.fileKey)}
          divider
          sx={{ height: itemHeight }}
        >
          <ListItemText
            primary={item.data.title}
            secondary={item.data.description}
            primaryTypographyProps={{
              variant: 'subtitle1',
              noWrap: true,
            }}
            secondaryTypographyProps={{
              variant: 'subtitle2',
              noWrap: true,
            }}
          />
        </ListItem>
      );
    };

    return (
      <MobilePanelList
        columns={columns.map((c) => ({
          name: c.header as string,
          searchKey: c.accessorKey as string,
        }))}
        withSearchBar
        heightOffset={TAB_LAYOUT_HEADER_HEIGHT}
        title={t('qrCodes.label', { ns: 'entities' })}
        data={qrCodes}
        renderItem={renderItem}
        itemHeight={itemHeight}
        menuOptions={[
          {
            onClick: () => handleCreateClick(),
            content: t('buttons.create', {
              name: t('qrCodes.label', { ns: 'entities' }),
            }),
            disabled: !canAddRemove,
            icon: <Add />,
          },
        ]}
      />
    );
  }

  return (
    <FormFrame loading={isLoading}>
      <MaterialReactTable table={table} />
    </FormFrame>
  );
};

export interface QRCodeActionRouteParams {
  displayMode: DisplayMode;
  tenantID: string | number;
  entityID: string | number;
}
export const getActionRouteForDisplayMode = ({
  tenantID,
  entityID,
  displayMode,
}: QRCodeActionRouteParams) => {
  switch (displayMode) {
    case 'property':
      return routeWithParams(ROUTES.PROPERTY_QR_CODES, [tenantID, entityID]);
    case 'group':
      return routeWithParams(ROUTES.PROPERTY_GROUP_QR_CODES, [
        tenantID,
        entityID,
      ]);
    default:
      return '';
  }
};

export const qrCodesAction: ActionFunction = async ({
  params,
  request,
}): Promise<TraxeroActionResponse<EntityPanelActionHandler>> => {
  const { body, handler } = await request.json();
  const { tenantID, propertyID, groupID, qrCodeID } =
    params as QRCodeRouteParams['params'];
  let entityType: EntityTypes;
  let entityID: number;

  if (propertyID) {
    entityType = EntityTypes.Property;
    entityID = parseInt(propertyID, 10);
  } else if (groupID) {
    entityType = EntityTypes.PropertyGroup;
    entityID = parseInt(groupID, 10);
  } else if (tenantID) {
    entityType = EntityTypes.Tenant;
    entityID = parseInt(tenantID, 10);
  } else {
    entityType = EntityTypes.None;
    entityID = 0;
  }

  let res: Response;
  try {
    switch (handler) {
      case EntityPanelActionHandler.UPDATE:
        res = await updateQRCode({
          tenantID,
          body,
          entityType,
          entityID,
          QRCodeID: qrCodeID,
        });
        break;
      case EntityPanelActionHandler.DELETE:
        res = await deleteQRCode({
          tenantID,
          body,
          entityType,
          entityID,
          QRCodeID: qrCodeID,
        });
        break;
      case EntityPanelActionHandler.GROUP_CREATE:
        res = await upsertEntityQRCodes({
          tenantID: tenantID || '',
          entityType: EntityTypes.PropertyGroup,
          entityID: groupID || '',
          qrCodeID: body.qrCodeID,
          body,
        });
        break;
      case EntityPanelActionHandler.TENANT_CREATE:
        res = await upsertEntityQRCodes({
          tenantID: tenantID || '',
          entityType: EntityTypes.Tenant,
          entityID: tenantID || '',
          qrCodeID: body.qrCodeID,
          body,
        });
        break;
      case EntityPanelActionHandler.PROPERTY_CREATE:
        res = await upsertEntityQRCodes({
          tenantID: tenantID || '',
          entityType: EntityTypes.Property,
          entityID: propertyID || '',
          qrCodeID: body.qrCodeID,
          body,
        });
        break;
      default: {
        res = new Response(null, {
          status: 404,
          statusText: 'Unknown Handler',
        });
      }
    }
  } catch (e) {
    res = new Response(null, { status: 500, statusText: 'Network Error' });
  }
  return { handler, status: res.status, statusText: res.statusText };
};
